// Restock notification signup modal — fires from any out-of-stock or
// coming-soon SKU. Anonymous visitors provide an email; signed-in
// members get their auth email pre-filled and can override.
//
// POST /api/consumer/research/notify { sku, email }
// BE contract lives in VOYA9_BE_Corporate_Catalogue_Contract.md.
import { useEffect, useState } from "react";
import Button from "@/components/Button";
import { useAuth } from "@/lib/auth";
import { notifyWhenAvailable } from "@/lib/researchApi";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";
import { RESEARCH_NAVY, RESEARCH_NAVY_DEEP } from "@/components/research/ResearchHero";

export default function NotifyMeModal({ item, isOpen, onClose }) {
  const { consumer } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Reset + prefill whenever a new item opens the modal.
  useEffect(() => {
    if (!isOpen) return;
    setEmail(consumer?.email || "");
    setDone(false);
    setSubmitting(false);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, item?.sku, consumer?.email, onClose]);

  if (!isOpen || !item) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(email.trim())) {
      toast("Please enter a valid email address.", "error");
      return;
    }
    setSubmitting(true);
    try {
      await notifyWhenAvailable({ sku: item.sku, email: email.trim() });
      setDone(true);
    } catch (err) {
      toast(err?.detail || err?.message || "Couldn't sign you up — please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={`notify-modal-${item.sku}`} role="dialog" aria-modal="true">
      <div
        onClick={onClose}
        data-testid="notify-modal-scrim"
        style={{ position: "fixed", inset: 0, background: "rgba(15,26,38,0.65)", zIndex: 100 }}
      />
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(460px, calc(100vw - 32px))",
          background: C.cream,
          zIndex: 101,
          padding: 32,
          boxShadow: "0 30px 80px rgba(15,26,38,0.35)",
          animation: "notifyFadeIn 200ms ease",
        }}
      >
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: RESEARCH_NAVY, textTransform: "uppercase" }}>
          ▪ RESTOCK NOTIFICATION
        </div>

        {done ? (
          <>
            <h2
              style={{
                margin: "16px 0 0",
                fontSize: 30,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                color: RESEARCH_NAVY_DEEP,
                fontFamily: '"DM Sans", sans-serif',
                lineHeight: 1.05,
              }}
            >
              You&apos;re{" "}
              <span
                style={{
                  fontFamily: '"Instrument Serif", Georgia, serif',
                  fontStyle: "italic",
                  fontWeight: 400,
                  color: C.peachDark,
                }}
              >
                on the list.
              </span>
            </h2>
            <p style={{ marginTop: 14, fontSize: 15, color: RESEARCH_NAVY_DEEP, lineHeight: 1.6 }}>
              We&apos;ll email <strong>{email}</strong> the moment <strong>{item.name}</strong> is back in stock.
            </p>
            <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
              <Button testId="notify-modal-close-done" variant="primary" onClick={onClose}>
                GOT IT →
              </Button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <h2
              id="notify-title"
              style={{
                margin: "16px 0 0",
                fontSize: 30,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                color: RESEARCH_NAVY_DEEP,
                fontFamily: '"DM Sans", sans-serif',
                lineHeight: 1.05,
              }}
            >
              Get pinged when{" "}
              <span
                style={{
                  fontFamily: '"Instrument Serif", Georgia, serif',
                  fontStyle: "italic",
                  fontWeight: 400,
                  color: C.peachDark,
                }}
              >
                {item.name}
              </span>{" "}
              is back.
            </h2>
            <p style={{ marginTop: 12, fontSize: 14, color: C.warmGray, lineHeight: 1.55 }}>
              One-time email. No newsletter, no follow-ups — just a heads-up the moment inventory returns.
            </p>
            <label
              htmlFor="notify-email"
              style={{ display: "block", marginTop: 24, fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: RESEARCH_NAVY, textTransform: "uppercase" }}
            >
              Email
            </label>
            <input
              id="notify-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="notify-modal-email-input"
              autoFocus
              style={{
                marginTop: 8,
                width: "100%",
                padding: "14px 16px",
                background: C.pure,
                border: `1.5px solid ${RESEARCH_NAVY}`,
                fontSize: 16,
                color: RESEARCH_NAVY_DEEP,
                fontFamily: "inherit",
              }}
            />
            <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={onClose}
                data-testid="notify-modal-cancel"
                style={{
                  background: "transparent",
                  border: `1px solid ${C.warmGray3}`,
                  color: RESEARCH_NAVY,
                  padding: "14px 20px",
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                CANCEL
              </button>
              <Button
                type="submit"
                variant="primary"
                testId="notify-modal-submit"
                disabled={submitting}
              >
                {submitting ? "SENDING…" : "NOTIFY ME →"}
              </Button>
            </div>
          </form>
        )}
      </div>
      <style>{`
        @keyframes notifyFadeIn {
          from { opacity: 0; transform: translate(-50%, -46%); }
          to   { opacity: 1; transform: translate(-50%, -50%); }
        }
      `}</style>
    </div>
  );
}
